import React, { useState } from "react";

import { useApiUrl, useDataProvider, useNotification } from "@refinedev/core";
import { ColumnDef } from "@tanstack/react-table";
import { Loader2, RefreshCw } from "lucide-react";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { IHit } from "@/types/common";
import { IGeodata } from "@/types/geodata";

function GeodataRetryButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false);
  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();
  const { open } = useNotification();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await custom?.({
        url: `${apiUrl}/geodata/enrich`,
        method: "post",
        payload: { id },
      });
      open?.({
        type: "success",
        message: "Enrichment triggered",
        description: "Geodata enrichment has been queued for processing.",
      });
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
    }
    setLoading(false);
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-xs gap-1"
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      Retry
    </Button>
  );
}

export const useGeodataColumns = () => {
  const columns = React.useMemo<ColumnDef<IHit<IGeodata>>[]>(
    () => [
      {
        id: "id",
        accessorKey: "document.id",
        header: "ID",
      },
      {
        id: "name",
        // accessorKey: "document.name",
        accessorFn: (row) =>
          row.highlights.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        header: "Name",
        minSize: 100,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div
              className="truncate"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            ></div>,
          );
        },
        enableSorting: true,
        size: 100,
      },
      {
        id: "country",
        header: "Country",
        accessorFn: (row) =>
          `${row?.document?.country} (${row?.document?.countryCode})`,
        enableSorting: true,
        size: 200,
      },
      {
        id: "type",
        header: "Type",
        accessorKey: "document.type",
        cell: function render({ getValue }: any) {
          return <Badge variant="default">{getValue()}</Badge>;
        },
        enableSorting: false,
        size: 100,
      },
      // No `status` column on /geodata, so the enrichment column goes in the
      // equivalent early-middle slot — right after `type` — rather than out by
      // `actions` where it used to sit and scroll off screen. Geodata is
      // NOT embeddable, so there is no embedding column here.
      enrichmentStatusColumn<IHit<IGeodata>>({
        size: 200,
        actions: ({ status, row }) =>
          status === "Failed" ? (
            <GeodataRetryButton id={row.document.id} />
          ) : null,
      }),
      {
        id: "region",
        accessorFn: (row) => row?.document?.region,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div className="font-bold truncate">{getValue()}</div>,
          );
        },
        header: "Region",
        enableSorting: false,
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row }) {
          return (
            <EditButton
              size="sm"
              hideText={true}
              recordItemId={row.original.document.id}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [],
  );
  return columns;
};
